import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useAuthContext } from "contexts/AuthContext"
import useDictionaryApi from "modules/dictionary"
import { languages as staticLanguages } from "utils/data/languages"

const normalizeStaticLanguages = () => {
  return staticLanguages.map((lang) => ({
    languageId: lang.id,
    name: lang.name,
    code: lang.id === 1 ? "es-MX" : lang.id === 3 ? "fr-FR" : "en-US",
  }))
}

export const useLanguageSelector = () => {
  const { i18n } = useTranslation()
  const { checkLanguage } = useAuthContext()
  const { getLanguages } = useDictionaryApi()

  const [anchorEl, setAnchorEl] = useState(null)
  const [languages, setLanguages] = useState(normalizeStaticLanguages())
  const open = Boolean(anchorEl)

  useEffect(() => {
    let active = true
    const fetchLanguages = async () => {
      try {
        const apiLanguages = await getLanguages()
        if (active && apiLanguages && apiLanguages.length > 0) {
          setLanguages(apiLanguages)
        }
      } catch (error) {
        console.warn(
          "Failed to fetch languages from API, using static fallbacks.",
          error
        )
      }
    }
    fetchLanguages()
    return () => {
      active = false
    }
  }, [getLanguages])

  const currentLanguage =
    languages.find((lang) => {
      const langCode = lang.code?.toLowerCase() || ""
      const activeCode = i18n.language?.toLowerCase() || ""
      return (
        activeCode === langCode || activeCode.startsWith(langCode.split("-")[0])
      )
    }) ||
    languages.find((lang) => lang.languageId === 2) ||
    languages[0]

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelectLanguage = useCallback(
    async (languageId) => {
      try {
        await checkLanguage(languageId)
      } catch (error) {
        console.error("Failed to change language:", error)
      }
    },
    [checkLanguage]
  )

  return {
    currentLanguageName: currentLanguage?.name,
    languages,
    open,
    anchorEl,
    handleOpen,
    handleClose,
    handleSelectLanguage,
  }
}
